// identity-core.js
// Platform-agnostic core of identity.js: key generation, fingerprint
// formatting, import/export — none of it touches persistence. Split out
// specifically so it (and anything that depends on it, like
// group-trust-core.js) can be bundled unchanged for a browser target.
// identity.js (Node) and src/browser/identity-browser.js both wrap this
// same core with their own storage — the actual cryptographic identity
// logic is never duplicated across platforms.

import { generateKeyPair, privateKeyFromRaw, publicKeyFromRaw } from '@libp2p/crypto/keys'
import { toString as uint8ArrayToString, fromString as uint8ArrayFromString } from 'uint8arrays'

/**
 * Load an existing identity from storage, or generate + persist a new one.
 * @param {{load: () => Promise<object|null>, save: (data: object) => Promise<void>}} storage
 */
export async function loadOrCreateIdentityFromStorage (storage) {
  const raw = await storage.load()
  if (raw) {
    const privateKey = privateKeyFromRaw(uint8ArrayFromString(raw.privateKey, 'base64pad'))
    return {
      privateKey,
      publicKey: privateKey.publicKey,
      fingerprint: fingerprintOf(privateKey.publicKey)
    }
  }

  const privateKey = await generateKeyPair('Ed25519')
  await storage.save({ privateKey: uint8ArrayToString(privateKey.raw, 'base64pad') })

  return {
    privateKey,
    publicKey: privateKey.publicKey,
    fingerprint: fingerprintOf(privateKey.publicKey)
  }
}

/**
 * Human-checkable fingerprint of a public key (like a Signal safety number,
 * simplified). Two users can read this aloud / compare out-of-band to defeat
 * a MITM on first contact.
 */
export function fingerprintOf (publicKey) {
  const raw = publicKey.raw ?? publicKey
  const hex = uint8ArrayToString(raw, 'hex')
  return hex.match(/.{1,4}/g).join(' ')
}

export function importPublicKey (base64) {
  return publicKeyFromRaw(uint8ArrayFromString(base64, 'base64pad'))
}

export function exportPublicKey (publicKey) {
  return uint8ArrayToString(publicKey.raw, 'base64pad')
}
